/** Selection range inside a markdown composer. Indices are UTF-16 code units. */
export interface TextRange {
  value: string;
  start: number;
  end: number;
}

export type MarkdownAction =
  | 'bold'
  | 'italic'
  | 'code'
  | 'heading'
  | 'ul'
  | 'ol'
  | 'quote'
  | 'fence'
  | 'link';

function clampRange(range: TextRange): TextRange {
  const length = range.value.length;
  const start = Math.max(0, Math.min(range.start, length));
  const end = Math.max(start, Math.min(range.end, length));
  return { value: range.value, start, end };
}

function replace(range: TextRange, insert: string, start: number, end: number): TextRange {
  return {
    value: range.value.slice(0, range.start) + insert + range.value.slice(range.end),
    start,
    end,
  };
}

export function wrapInline(
  raw: TextRange,
  before: string,
  after: string,
  placeholder: string,
): TextRange {
  const range = clampRange(raw);
  const selected = range.value.slice(range.start, range.end);

  if (
    range.start >= before.length &&
    range.value.slice(range.start - before.length, range.start) === before &&
    range.value.slice(range.end, range.end + after.length) === after
  ) {
    const next = range.value.slice(0, range.start - before.length) + selected + range.value.slice(range.end + after.length);
    return {
      value: next,
      start: range.start - before.length,
      end: range.end - before.length,
    };
  }

  if (
    selected.startsWith(before) &&
    selected.endsWith(after) &&
    selected.length >= before.length + after.length
  ) {
    const inner = selected.slice(before.length, selected.length - after.length);
    return replace(range, inner, range.start, range.start + inner.length);
  }

  if (selected.length === 0) {
    const insert = `${before}${placeholder}${after}`;
    return replace(
      range,
      insert,
      range.start + before.length,
      range.start + before.length + placeholder.length,
    );
  }

  return replace(
    range,
    `${before}${selected}${after}`,
    range.start + before.length,
    range.start + before.length + selected.length,
  );
}

function lineBounds(value: string, start: number, end: number): { from: number; to: number } {
  const from = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  if (end === 0) return { from, to: 0 };
  let cursor = end;
  if (cursor > from && value[cursor - 1] === '\n') cursor -= 1;
  const newline = value.indexOf('\n', cursor);
  return { from, to: newline === -1 ? value.length : newline };
}

export function prefixLines(raw: TextRange, prefix: string): TextRange {
  const range = clampRange(raw);
  const { from, to } = lineBounds(range.value, range.start, range.end);
  const block = range.value.slice(from, to);
  const lines = block.split('\n');
  const eligible = lines.filter((line) => line.length > 0);
  const allPrefixed = eligible.length > 0 && eligible.every((line) => line.startsWith(prefix));
  const nextLines = lines.map((line) => {
    if (line.length === 0) return line;
    return allPrefixed ? line.slice(prefix.length) : `${prefix}${line}`;
  });
  const nextBlock = nextLines.join('\n');
  return {
    value: range.value.slice(0, from) + nextBlock + range.value.slice(to),
    start: from,
    end: from + nextBlock.length,
  };
}

function wrapFence(raw: TextRange, placeholder = 'code'): TextRange {
  const range = clampRange(raw);
  const selected = range.value.slice(range.start, range.end);
  const body = selected.length > 0 ? selected : placeholder;
  const insert = `\`\`\`\n${body}\n\`\`\``;
  const bodyStart = range.start + 4;
  return replace(range, insert, bodyStart, bodyStart + body.length);
}

function insertLink(raw: TextRange, placeholder = 'link text'): TextRange {
  const range = clampRange(raw);
  const selected = range.value.slice(range.start, range.end);
  const text = selected.length > 0 ? selected : placeholder;
  const insert = `[${text}](url)`;
  const urlStart = range.start + text.length + 3;
  return replace(range, insert, urlStart, urlStart + 3);
}

export function applyMarkdownAction(range: TextRange, action: MarkdownAction): TextRange {
  switch (action) {
    case 'bold':
      return wrapInline(range, '**', '**', 'bold text');
    case 'italic':
      return wrapInline(range, '*', '*', 'italic text');
    case 'code':
      return wrapInline(range, '`', '`', 'code');
    case 'heading':
      return prefixLines(range, '## ');
    case 'ul':
      return prefixLines(range, '- ');
    case 'ol':
      return prefixLines(range, '1. ');
    case 'quote':
      return prefixLines(range, '> ');
    case 'fence':
      return wrapFence(range);
    case 'link':
      return insertLink(range);
  }
}

/**
 * Drop `text` in at the caret on a line of its own. Anything selected is replaced. A block
 * image or a pasted-upload placeholder sits badly mid-sentence, so a newline is added on
 * each side where one is missing; the caret lands after the block, on the following line.
 */
export function insertBlock(raw: TextRange, text: string): TextRange {
  const range = clampRange(raw);
  const before = range.value.slice(0, range.start);
  const after = range.value.slice(range.end);
  const lead = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
  const trail = after.length > 0 && !after.startsWith('\n') ? '\n' : after.length === 0 ? '\n' : '';
  const insert = `${lead}${text}${trail}`;
  const caret = range.start + insert.length;
  return { value: before + insert + after, start: caret, end: caret };
}
